import {
  Users,
  Settings2,
  Cog,
  UserCheck,
  Target,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";

const ICONS = {
  user_impact: Users,
  affected_service: Settings2,
  suspected_component: Cog,
  likely_owner: UserCheck,
  recommended_action: Target,
  human_approval: ShieldCheck,
};

const STATUS_STYLES = {
  unknown:   { dot: "bg-slate-300", text: "text-slate-500", border: "border-slate-200", chip: "bg-slate-100 text-slate-500" },
  captured:  { dot: "bg-sky-500",   text: "text-sky-700",   border: "border-sky-500/30", chip: "bg-sky-500/10 text-sky-700" },
  inferred:  { dot: "bg-violet-500", text: "text-violet-700", border: "border-violet-500/30", chip: "bg-violet-500/10 text-violet-700" },
  confirmed: { dot: "bg-emerald-500", text: "text-emerald-700", border: "border-emerald-500/30", chip: "bg-emerald-500/10 text-emerald-700" },
  pending:   { dot: "bg-amber-500", text: "text-amber-700", border: "border-amber-500/30", chip: "bg-amber-500/10 text-amber-700" },
};

export default function IncidentFlowView({ flow, compact = false }) {
  if (!flow?.nodes?.length) return null;
  return (
    <div
      data-testid="incident-flow-view"
      className={`cre-surface rounded-sm ${compact ? "p-4" : "p-5 md:p-6"}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="cre-overline text-sky-700">Incident flow</div>
          <h3 className="font-heading text-lg font-bold tracking-tight mt-1">
            Impact → Approval chain
          </h3>
          <p className="mt-1 text-xs text-slate-500 max-w-2xl">
            Status, confidence, and evidence at every step from user impact
            through to human approval.
          </p>
        </div>
      </div>

      {/* Desktop horizontal flow */}
      <ol className="hidden lg:grid grid-cols-6 gap-2" data-testid="flow-row-desktop">
        {flow.nodes.map((node, idx) => (
          <FlowNode
            key={node.key}
            node={node}
            isLast={idx === flow.nodes.length - 1}
            compact={compact}
          />
        ))}
      </ol>

      {/* Mobile / narrow vertical flow */}
      <ol className="lg:hidden space-y-2.5" data-testid="flow-row-mobile">
        {flow.nodes.map((node) => (
          <FlowNode key={node.key} node={node} vertical compact />
        ))}
      </ol>
    </div>
  );
}

function FlowNode({ node, isLast, vertical, compact }) {
  const Icon = ICONS[node.key] || Cog;
  const style = STATUS_STYLES[node.status] || STATUS_STYLES.unknown;
  const conf = Math.round((node.confidence || 0) * 100);
  return (
    <li
      data-testid={`flow-node-${node.key}`}
      className={`relative border rounded-sm ${style.border} bg-white p-3 ${
        vertical ? "" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        <div
          className={`shrink-0 h-7 w-7 rounded-sm border ${style.border} flex items-center justify-center bg-white`}
        >
          <Icon className={`h-3.5 w-3.5 ${style.text}`} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="cre-overline text-slate-500">{node.label}</span>
            <span
              className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${style.chip}`}
              data-testid={`flow-node-${node.key}-status`}
            >
              {node.status}
            </span>
          </div>
          <div
            className="mt-1 text-xs font-semibold text-slate-800 leading-snug break-words"
            data-testid={`flow-node-${node.key}-value`}
          >
            {node.value}
          </div>
          {!compact && node.evidence?.length ? (
            <ul className="mt-1.5 text-[10px] text-slate-500 list-disc ml-3 space-y-0.5">
              {node.evidence.slice(0, 2).map((e, i) => (
                <li key={i} className="break-words">{e}</li>
              ))}
            </ul>
          ) : null}
          {conf > 0 ? (
            <div className="mt-1 text-[10px] font-mono text-slate-400">
              confidence {conf}%
            </div>
          ) : null}
        </div>
      </div>
      {!isLast && !vertical ? (
        <ChevronRight
          className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300"
          strokeWidth={2}
        />
      ) : null}
    </li>
  );
}
